import { useState } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  Stack,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import checkmarkFill from "@iconify/icons-eva/checkmark-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import undoFill from "@iconify/icons-eva/undo-fill";
import edit2Outline from "@iconify/icons-eva/edit-2-outline";
import alertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import Label from "src/components/Label";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { formatAsCLP, formatValorPlan } from "src/utils/formatNumber";
import {
  COLOR_ESTADO,
  LABEL_ESTADO,
  LABEL_RECONCILIACION,
  estaDetenido,
} from "src/components/facturacion/estadoFacturacion";
import { useSnackbar } from "src/components/snackbar";
import {
  useEditarCantidadItemMutation,
  useReintentarFacturacionMutation,
} from "src/services/facturasApi";
import { mensajeDeError, statusDeError } from "src/utils/manejo-errores";

const MONEDAS_PESO = ["PESOS", "PESO", "CLP"];
const esMonedaNominal = (m) =>
  m && !MONEDAS_PESO.includes(String(m).toUpperCase());

// Formatea un valor en su moneda nominal: pesos -> "$X"; UF/USD/etc -> "UF x,x".
const formatNominal = (v, tipoMoneda) =>
  esMonedaNominal(tipoMoneda) ? formatValorPlan(v, tipoMoneda) : formatAsCLP(v);

export default function FilaFacturacion({
  item,
  sucursal,
  soloLectura,
  permiso,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [editar, { isLoading: guardando }] = useEditarCantidadItemMutation();
  const [reintentar, { isLoading: reintentando }] =
    useReintentarFacturacionMutation();
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(item.cantidad ?? 1);

  // El backend presenta como ERROR al ítem detenido, así que sin este corte la fila ofrecería
  // un reintento que el servidor rechaza —y que, si lo aceptara, podría emitir dos veces el
  // mismo documento—. En su lugar se ofrece ver el motivo, que es lo único accionable hoy.
  const detenido = estaDetenido(item);
  const puedeReintentar =
    !soloLectura &&
    permiso?.puedeReintentar &&
    item.estado === "ERROR" &&
    !detenido;

  const onVerDetencion = () => {
    const marca =
      LABEL_RECONCILIACION[item.estadoReconciliacion] ??
      item.estadoReconciliacion;
    // El detalle es el único rastro local del documento que quedó colgado. Cuando falta, se
    // nombra igual la marca: sin ella no hay por dónde empezar a resolverlo.
    const detalle =
      item.detalleReconciliacion || "No se registró un detalle para este ítem";
    AlertaModalV2({
      title: "Ítem detenido por reconciliación",
      text: `${marca}: ${detalle.replace(/[.;\s]+$/, "")}. Mientras siga así no se puede reintentar, porque el documento pudo haberse emitido y un reintento lo duplicaría.`,
      icon: "info",
      confirmButtonText: "Entendido",
      showCancelButton: false,
    }).fire();
  };

  const onReintentarFila = async () => {
    try {
      await reintentar({ sucursal, items: [item.id] }).unwrap();
      enqueueSnackbar(
        "Ítem marcado para reintentar. Se incluirá en la próxima facturación.",
        { variant: "success" },
      );
    } catch (e) {
      // Un 409 acá puede ser una emisión en curso, un ítem que quedó detenido entre el
      // render y el clic, o uno que dejó de estar en error: el backend dice cuál, y el texto
      // propio queda de respaldo por si la respuesta no trae mensaje.
      const msg =
        statusDeError(e) === 409
          ? mensajeDeError(
              e,
              "Hay una facturación en curso; espere para reintentar.",
            )
          : "No se pudo reintentar el ítem.";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  // Solo un PENDIENTE virgen (intentos=0, sin folio) es editable; y nunca en read-only.
  const editable =
    !soloLectura &&
    permiso?.puedeEditarCantidad &&
    item.estado === "PENDIENTE" &&
    item.intentos === 0 &&
    !item.folio;

  const guardar = async () => {
    const cantidad = Number(valor);
    if (!Number.isInteger(cantidad) || cantidad < 1) {
      enqueueSnackbar(
        "La cantidad debe ser un número entero mayor o igual a 1.",
        {
          variant: "warning",
        },
      );
      return;
    }
    try {
      await editar({ sucursal, id: item.id, cantidad }).unwrap();
      setEditando(false);
      enqueueSnackbar("Cantidad actualizada.", { variant: "success" });
    } catch (e) {
      const st = statusDeError(e);
      // 409 y 422 cubren varios motivos cada uno —emisión activa, ítem que dejó de ser
      // editable, ítem sin referencia al plan—: se muestra el que informa el backend y el
      // texto propio queda de respaldo.
      const msg =
        st === 409
          ? mensajeDeError(
              e,
              "Hay una facturación en curso; no se puede editar ahora.",
            )
          : st === 422
            ? mensajeDeError(
                e,
                'Este ítem ya no es editable (emitido o en error). Use "Reintentar" si está en error.',
              )
            : "No se pudo actualizar la cantidad.";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
          {item.nombreCliente}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: 220 }}
          color="text.secondary"
        >
          {item.nombrePlan}
        </Typography>
      </TableCell>
      <TableCell>
        <Label
          variant="ghost"
          color={item.tipoFrecuencia === "MES" ? "primary" : "info"}
        >
          {item.tipoFrecuencia === "MES" ? "Mensual" : "Anual"}
        </Label>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Label variant="ghost" color={COLOR_ESTADO[item.estado] ?? "default"}>
            {LABEL_ESTADO[item.estado] ?? item.estado}
          </Label>
          {item.estado === "EMITIENDO" && <CircularProgress size={14} />}
        </Stack>
        {detenido ? (
          <Typography variant="caption" color="warning.main" display="block">
            Detenido — documento por reconciliar
          </Typography>
        ) : item.estado === "ERROR" && item.errorAmbiguo ? (
          <Typography variant="caption" color="warning.main" display="block">
            Error de red — puede requerir verificación
          </Typography>
        ) : null}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{item.folio ? item.folio : "—"}</Typography>
      </TableCell>
      <TableCell align="right">
        {editando ? (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
          >
            <TextField
              type="text"
              size="small"
              value={valor}
              // Solo digitos; sin spinner ni scroll que altere el valor por error.
              onChange={(e) => setValor(e.target.value.replace(/\D/g, ""))}
              inputProps={{
                inputMode: "numeric",
                style: { width: 64, textAlign: "right" },
              }}
              autoFocus
            />
            <IconButton
              size="small"
              color="primary"
              disabled={guardando}
              onClick={guardar}
            >
              <Icon icon={checkmarkFill} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setEditando(false);
                setValor(item.cantidad ?? 1);
              }}
            >
              <Icon icon={closeFill} />
            </IconButton>
          </Stack>
        ) : editable ? (
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Typography variant="body2">{item.cantidad ?? 1}</Typography>
            <Tooltip title="Editar cantidad">
              <IconButton
                size="small"
                onClick={() => {
                  setValor(item.cantidad ?? 1);
                  setEditando(true);
                }}
                sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }}
              >
                <Icon icon={edit2Outline} width={15} height={15} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography variant="body2">{item.cantidad ?? 1}</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatAsCLP(item.valorTotal)}</Typography>
        {item.cantidad > 1 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {item.cantidad} x {formatAsCLP(item.valorUnitario)}
          </Typography>
        )}
        {esMonedaNominal(item.tipoMoneda) && (
          <Typography variant="caption" color="text.secondary" display="block">
            {formatValorPlan(
              (item.valorDesc ?? 0) * (item.cantidad ?? 1),
              item.tipoMoneda,
            )}
          </Typography>
        )}
        {item.descuentoActivo &&
          (item.valorFijo === "SI" && item.valor > item.valorDesc ? (
            // Original confiable -> mostrar precio unitario tachado y con descuento.
            <Typography
              variant="caption"
              color="success.main"
              display="block"
              sx={{ mt: 0.5 }}
            >
              <Box
                component="span"
                sx={{
                  textDecoration: "line-through",
                  color: "text.disabled",
                  mr: 0.5,
                }}
              >
                {formatNominal(item.valor, item.tipoMoneda)}
              </Box>
              {formatNominal(item.valorDesc, item.tipoMoneda)}{" "}
              {`(-${item.descuento}%)`}
            </Typography>
          ) : (
            <Label variant="ghost" color="success" sx={{ mt: 0.5 }}>
              {`-${item.descuento}% dcto.`}
            </Label>
          ))}
      </TableCell>
      <TableCell align="right">
        {detenido && (
          <Tooltip title="Ver por qué está detenido">
            <IconButton size="small" color="warning" onClick={onVerDetencion}>
              <Icon icon={alertTriangleFill} />
            </IconButton>
          </Tooltip>
        )}
        {puedeReintentar && (
          <Tooltip title={item.ultimoError || "Reintentar"}>
            <span>
              <IconButton
                size="small"
                color="warning"
                disabled={reintentando}
                onClick={onReintentarFila}
              >
                {reintentando ? (
                  <CircularProgress size={16} />
                ) : (
                  <Icon icon={undoFill} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
